<script setup>
import { computed, ref, useSlots } from 'vue'
import IconBase from '../icon/IconBase.vue';

const props = defineProps({
  content: {
    type: String,
  },
  wrapperClass: {
    type: String,
    default: 'inline'
  },
  interactive: {
    type: Boolean,
    default: false,
  },
  hide: {
    type: Number,
    default: 300,
  },
  active: {
    type: Boolean,
    default: true
  },
  html: {
    type: Boolean,
    default: false,
  },
  popperClass: {
    type: String,
    default: 'max-w-[200px] text-sm break-words'
  },
  // Wrap the trigger so tooltips still work when the slotted control is disabled.
  wrapTrigger: {
    type: Boolean,
    default: false,
  },
})

const slots = useSlots()
const hasPopperContent = computed(() => {
  if (props.content && String(props.content).trim()) return true
  return !!slots.content
})

const wrapTriggerClass = computed(() => (
  props.wrapperClass?.includes('w-full') ? 'flex w-full h-full min-w-0' : 'inline-flex max-w-full'
))
</script>
<template>
  <div :class="wrapperClass">
    <VTooltip
      :class="wrapperClass"
      :popperClass="[props.interactive ? 'castr-tooltip' : '', popperClass]"
      v-if="props.active && hasPopperContent"
      :triggers="['hover', 'focus']"
      :popper-triggers="props.interactive ? ['hover'] : []"
      :delay="{ show: 100, hide: props.interactive ? Math.max(props.hide, 400) : props.hide }">
      <span v-if="wrapTrigger" :class="wrapTriggerClass">
        <slot />
      </span>
      <slot v-else />

      <template #popper v-if="hasPopperContent && props.html">
        <div v-if="props.content" v-html="props.content"></div>
        <slot name="content" />
      </template>
      <template #popper v-else-if="hasPopperContent">
        {{ props.content }}
        <slot name="content" />
      </template>
    </VTooltip>
    <template v-else>
      <slot />
    </template>
  </div>
</template>

<!-- Global (un-scoped): floating-vue teleports the popper to <body>, so the
     themed styles for interactive tooltips can't be scoped to this component. -->
<style>
.v-popper__popper.castr-tooltip .v-popper__inner {
  background: #000;
  color: var(--c-dark-9, #e7eaf3);
  border: 1px solid #000;
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  font-size: 13px;
  line-height: 1.4;
}

.v-popper__popper.castr-tooltip .v-popper__arrow-outer,
.v-popper__popper.castr-tooltip .v-popper__arrow-inner {
  border-color: #000;
}
</style>